/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik, FormikProvider, FieldArray} from 'formik'

import {KTSVG, PurchaseOrderStatus} from '../../../helpers'
import {KTCard, KTCardBody, Select, SelectCreatable, Loading, Datepicker} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {purchaseOrderCreate} from '../../../redux/slices/purchase.slice'
import {
  allCompanyLookup,
  projectLookup,
  allProductLookup,
  basicLookup,
  staffGetAll,
} from '../../../redux/slices/lookup.slice'
import {productGetInfo, productCreateFast} from '../../../redux/slices/product.slice'

import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  productLookup: state.lookup.productLookup,
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  ProductUnit: state.lookup.ProductUnit,
  staffLookup: state.lookup.staffLookup,
  loading: state.lookup.loading
})

const selectProductData = (state) => ({
  detail: state.product.detail,
  isRedirect: state.product.isRedirect
})

const selectPurchaseData = (state) => ({
  isRedirect: state.purchase.isRedirect,
  loading: state.purchase.loading
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  
  const pageUrl = useMemoizedPageUrl(location)

  const {productLookup, projectLookupList, companyLookup, ProductUnit, staffLookup, loading} =
    useSelector(selectLookupData)
  const {detail: product, isRedirect: createProduct} = useSelector(selectProductData)
  const {isRedirect, loading: createLoading} = useSelector(selectPurchaseData)
  const dispatch = useDispatch()
  const [changeProduct, setChangeProduct] = useState()
  const [createItem, setCreateItem] = useState()

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(projectLookup())
    dispatch(allProductLookup())
    dispatch(basicLookup('ProductUnit'))
    dispatch(staffGetAll())
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    PurchaseOrderStatusId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Name: '',
    ProjectId: null,
    CompanyId: null,
    IssueDate: new Date(),
    ShipmentDate: new Date(),
    DeliveryDate: new Date(),
    PurchaseOrderStatusId: '',
    DeliveryAddress: '',
    RelatedStaffId: null,
    Note: '',
    PurchaseOrderItems: [
      {
        ProductId: null,
        Amount: 0,
        ProductUnitId: 1,
        Description: '',
      },
    ],
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(purchaseOrderCreate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.ORDER'}),
      path: '/purchase/order',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  useEffect(() => {
    if (isRedirect?.id) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect?.id}`)
  }, [isRedirect, navigate, pageUrl])

  const onChangeItem = useCallback((target, value, index) => {
    formik.setFieldValue(`PurchaseOrderItems[${index}][${target}]`, value)
  }, [formik])

  const onClickNewItem = useCallback((arrayHelpers) => {
    arrayHelpers.push({
      ProductId: null,
      Amount: 0,
      ProductUnitId: 1,
      Description: '',
    })
  }, [])

  const onChangeProduct = useCallback((item, index) => {
    formik.setFieldValue(`PurchaseOrderItems[${index}].ProductId`, Number(item?.value))
    setChangeProduct(index)
    dispatch(productGetInfo({Id: item?.value}))
  }, [dispatch])

  useEffect(() => {
    if (product)
      formik.setFieldValue(
        `PurchaseOrderItems[${changeProduct}].ProductUnitId`,
        product?.productUnitId
      )
  }, [product, changeProduct])

  const onClickFastProduct = useCallback((Name, Index) => {
    dispatch(productCreateFast({Name}))
    setCreateItem({Name, Index})
  }, [dispatch])

  useEffect(() => {
    if (createProduct) dispatch(allProductLookup())
  }, [createProduct, dispatch])

  useEffect(() => {
    if (productLookup && createProduct) {
      formik.setFieldValue(`PurchaseOrderItems[${createItem?.Index}].ProductId`, createProduct.id)
    }
  }, [productLookup, createProduct, createItem])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.ORDER.ADD'})}</PageTitle>
      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard>
            <KTCardBody>
              <FormikProvider value={formik}>
                <form className='form' onSubmit={formik.handleSubmit} noValidate>
                  <div className='row mb-6'>
                    <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'ORDER.CREATE.NAME'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        required
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('Name')}
                      />
                      {formik.touched.Name && formik.errors.Name && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.Name}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'ORDER.CREATE.PROJECTID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={projectLookupList}
                        required
                        {...formik.getFieldProps('ProjectId')}
                        onChange={(item) => formik.setFieldValue('ProjectId', Number(item?.value))}
                      />
                      {formik.touched.ProjectId && formik.errors.ProjectId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.ProjectId}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'ORDER.CREATE.PURCHASEREQUESTSTATUSID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={PurchaseOrderStatus}
                        required
                        {...formik.getFieldProps('PurchaseOrderStatusId')}
                        onChange={(item) =>
                          formik.setFieldValue('PurchaseOrderStatusId', Number(item?.value))
                        }
                        textType={'intl'}
                      />
                      {formik.touched.PurchaseOrderStatusId && formik.errors.PurchaseOrderStatusId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.PurchaseOrderStatusId}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'ORDER.CREATE.COMPANYID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={companyLookup}
                        {...formik.getFieldProps('CompanyId')}
                        onChange={(item) => formik.setFieldValue('CompanyId', Number(item?.value))}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'ORDER.CREATE.ISSUEDATE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Datepicker
                        {...formik.getFieldProps('IssueDate')}
                        onChange={([date]) => formik.setFieldValue('IssueDate', date)}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'ORDER.CREATE.SHIPMENTDATE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Datepicker
                        {...formik.getFieldProps('ShipmentDate')}
                        onChange={([date]) => formik.setFieldValue('ShipmentDate', date)}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'ORDER.CREATE.DELIVERYDATE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Datepicker
                        {...formik.getFieldProps('DeliveryDate')}
                        onChange={([date]) => formik.setFieldValue('DeliveryDate', date)}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'ORDER.CREATE.DELIVERYADDRESS'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <textarea
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        disabled={formik.isSubmitting}
                        rows={4}
                        {...formik.getFieldProps('DeliveryAddress')}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'ORDER.CREATE.RELATEDSTAFFID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={staffLookup}
                        required
                        {...formik.getFieldProps('RelatedStaffId')}
                        onChange={(item) =>
                          formik.setFieldValue('RelatedStaffId', Number(item?.value))
                        }
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'ORDER.CREATE.NOTE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <textarea
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        disabled={formik.isSubmitting}
                        rows={4}
                        {...formik.getFieldProps('Note')}
                      />
                    </div>
                  </div>

                  <FieldArray
                    name='PurchaseOrderItems'
                    render={(arrayHelpers) => (
                      <>
                        <div className='table-responsive'>
                          <table className='table g-5 gs-0 mb-0 fw-bold text-gray-700'>
                            <thead>
                              <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                                <th>
                                  {intl.formatMessage({
                                    id: 'ORDER.CREATE.PURCHASEORDERITEMS.PRODUCTID',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'ORDER.CREATE.PURCHASEORDERITEMS.AMOUNT',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'ORDER.CREATE.PURCHASEORDERITEMS.PRODUCTUNITID',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'ORDER.CREATE.PURCHASEORDERITEMS.DESCRIPTION',
                                  })}
                                </th>
                                <th>&nbsp;</th>
                              </tr>
                            </thead>
                            <tbody>
                              {formik.values.PurchaseOrderItems.map((item, index) => (
                                <tr className='border-bottom border-bottom-dashed' key={index}>
                                  <td className='pe-7 ps-1'>
                                    <SelectCreatable
                                      data={productLookup}
                                      name={`PurchaseOrderItems[${index}].ProductId`}
                                      value={formik.values.PurchaseOrderItems[index].ProductId}
                                      required
                                      onChange={(item) => onChangeProduct(item, index)}
                                      onCreate={(item) => onClickFastProduct(item, index)}
                                    />
                                  </td>

                                  <td className='ps-0'>
                                    <input
                                      type='number'
                                      className='form-control form-control-solid'
                                      autoComplete='off'
                                      disabled={formik.isSubmitting}
                                      placeholder={intl.formatMessage({
                                        id: 'ORDER.CREATE.PURCHASEORDERITEMS.AMOUNT',
                                      })}
                                      name={`PurchaseOrderItems[${index}].Amount`}
                                      value={formik.values.PurchaseOrderItems[index].Amount}
                                      required
                                      onChange={(event) =>
                                        onChangeItem('Amount', Number(event.target.value), index)
                                      }
                                      onFocus={(event) => event.target.select()}
                                    />
                                  </td>

                                  <td className='ps-0'>
                                    <Select
                                      data={ProductUnit}
                                      name={`PurchaseOrderItems[${index}].ProductUnitId`}
                                      value={formik.values.PurchaseOrderItems[index].ProductUnitId}
                                      required
                                      onChange={(item) =>
                                        onChangeItem('ProductUnitId', Number(item?.value), index)
                                      }
                                    />
                                  </td>

                                  <td className='ps-0'>
                                    <input
                                      type='text'
                                      className='form-control form-control-solid'
                                      autoComplete='off'
                                      disabled={formik.isSubmitting}
                                      placeholder={intl.formatMessage({
                                        id: 'ORDER.CREATE.PURCHASEORDERITEMS.DESCRIPTION',
                                      })}
                                      name={`PurchaseOrderItems[${index}].Description`}
                                      value={formik.values.PurchaseOrderItems[index].Description}
                                      onChange={formik.handleChange}
                                    />
                                  </td>

                                  <td className='pt-5 text-end'>
                                    <div className='d-flex'>
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-icon btn-active-color-primary'
                                        onClick={() => arrayHelpers.remove(index)}
                                      >
                                        <KTSVG
                                          path='/images/icons/duotune/general/gen027.svg'
                                          className='svg-icon-2'
                                        />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className='border-top border-top-dashed align-middle fs-6 fw-bold text-gray-700'>
                                <th colSpan={4}>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-outline btn-outline-dashed btn-outline-success btn-active-light-success'
                                    onClick={() => onClickNewItem(arrayHelpers)}
                                  >
                                    <KTSVG
                                      path='/images/icons/duotune/arrows/arr009.svg'
                                      className='svg-icon-2'
                                    />
                                    {intl.formatMessage({id: 'ADD_NEW_LINE'})}
                                  </button>
                                </th>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </>
                    )}
                  />

                  <div className='row pt-15'>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                      <button
                        className='btn btn-light'
                        onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                      >
                        {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                      </button>
                      <button
                        type='submit'
                        className='btn btn-primary'
                        data-kt-users-modal-action='submit'
                        disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                      >
                        <span className='indicator-label'>
                          {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                        </span>
                        {formik.isSubmitting && (
                          <span className='indicator-progress'>
                            {intl.formatMessage({id: 'PLEASE'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </FormikProvider>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
